import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { resolveUnitPrice } from "./pricing";
import { api } from "./api";

const CartCtx = createContext(null);
const STORAGE_KEY = "lecis_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState(null); // { valid, code, discount, description }
  const [shippingMethods, setShippingMethods] = useState([]);
  const [shippingRequired, setShippingRequired] = useState(false);
  const [shippingMethodId, setShippingMethodId] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product_id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      return [...prev, {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        price: product.price,
        price_tiers: product.price_tiers || [],
        image: (product.images && product.images[0]) || null,
        slug: product.slug,
        quantity: qty,
      }];
    });
    setOpen(true);
  };

  const remove = (product_id) => setItems((prev) => prev.filter((it) => it.product_id !== product_id));
  const updateQty = (product_id, qty) => setItems((prev) => prev.map((it) => it.product_id === product_id ? { ...it, quantity: Math.max(1, qty) } : it));
  const clear = () => { setItems([]); setCoupon(null); setShippingMethodId(null); };

  // Subtotal uses the resolved (tiered) unit price for each line's quantity.
  const subtotal = useMemo(() => items.reduce((s, it) => s + resolveUnitPrice(it.price, it.price_tiers, it.quantity) * it.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);

  // Apply a single coupon (no stacking). Server re-prices + validates.
  const applyCoupon = async (code) => {
    try {
      const { data } = await api.post("/coupons/validate", {
        code,
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
      });
      if (data.valid) { setCoupon(data); return { ok: true }; }
      setCoupon(null);
      return { ok: false, message: data.message };
    } catch (e) {
      setCoupon(null);
      return { ok: false, message: "Could not validate coupon" };
    }
  };
  const removeCoupon = () => setCoupon(null);

  // Re-validate the applied coupon whenever the cart changes (qty/items),
  // so the discount stays correct and stale/invalid coupons auto-clear.
  useEffect(() => {
    if (!coupon?.code) return;
    if (items.length === 0) { setCoupon(null); return; }
    let cancelled = false;
    api.post("/coupons/validate", { code: coupon.code, items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })) })
      .then(({ data }) => { if (!cancelled) setCoupon(data.valid ? data : null); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const discount = coupon?.valid ? Number(coupon.discount) : 0;

  // Fetch a server-side shipping quote whenever the cart or coupon changes.
  // shippingRequired=false means the admin has configured no valid method →
  // storefront proceeds exactly as before (no shipping selection, cost 0).
  useEffect(() => {
    if (items.length === 0) {
      setShippingMethods([]); setShippingRequired(false); setShippingMethodId(null);
      return;
    }
    let cancelled = false;
    api.post("/shipping/quote", {
      items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
      coupon_code: coupon?.valid ? coupon.code : null,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const methods = data.methods || [];
        setShippingMethods(methods);
        setShippingRequired(!!data.shipping_required);
        // Drop a stale selection that is no longer valid; never auto-select.
        setShippingMethodId((cur) => (methods.some((m) => m.id === cur) ? cur : null));
      })
      .catch(() => { if (!cancelled) { setShippingMethods([]); setShippingRequired(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, coupon?.code, coupon?.valid, discount]);

  const selectedShippingMethod = useMemo(
    () => shippingMethods.find((m) => m.id === shippingMethodId) || null,
    [shippingMethods, shippingMethodId]
  );
  const shippingCost = selectedShippingMethod ? Number(selectedShippingMethod.cost) : 0;
  const total = Math.max(0, subtotal - discount) + shippingCost;

  return (
    <CartCtx.Provider value={{ items, add, remove, updateQty, clear, subtotal, count, open, setOpen, coupon, applyCoupon, removeCoupon, discount, total, shippingMethods, shippingRequired, shippingMethodId, setShippingMethodId, selectedShippingMethod, shippingCost }}>
      {children}
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);
